import React, { useState } from 'react';
import { Card, CardHeader, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import SafeIcon from '../common/SafeIcon';
import { motion, AnimatePresence } from 'framer-motion';
import { generateBoardPresentation, mockBoardInput } from '../data/boardPresentationLogic';

export const BoardPresentationGenerator = () => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [presentation, setPresentation] = useState(null);
  const [activeSlide, setActiveSlide] = useState(0);

  const handleGenerate = () => {
    setIsGenerating(true);
    setTimeout(() => {
      setPresentation(generateBoardPresentation(mockBoardInput));
      setIsGenerating(false);
    }, 2500);
  };

  return (
    <div className="space-y-8 pb-20">
      <div className="flex justify-between items-end">
        <div>
          <h1 className="text-2xl font-black text-slate-900 uppercase tracking-tight">AI Board Presentation</h1>
          <p className="text-slate-500 mt-1 uppercase text-[10px] font-bold tracking-widest">Executive Reporting Engine v5.0</p>
        </div>
        {presentation && (
          <div className="flex gap-2">
            <Button variant="secondary" icon={<SafeIcon name="Download" />}>PPTX Eksport</Button>
            <Button variant="primary" icon={<SafeIcon name="Share2" />}>Send til Styreportal</Button>
          </div>
        )}
      </div>

      <AnimatePresence mode="wait">
        {!presentation ? (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="h-[65vh] flex items-center justify-center">
            <Card className="bg-white border border-slate-200 max-w-2xl w-full overflow-hidden relative shadow-[0_32px_64px_-15px_rgba(0,0,0,0.1)]">
              <CardContent className="p-20 text-center space-y-12 relative z-10">
                <div className="w-24 h-24 bg-indigo-50 rounded-[32px] flex items-center justify-center mx-auto border border-indigo-100 shadow-sm">
                  <SafeIcon name="Award" className="text-5xl text-indigo-600" />
                </div>
                <div className="space-y-6">
                  <h2 className="text-5xl font-black tracking-tighter uppercase leading-[0.9] text-slate-900">Klar for Strategisk<br/>Styrerapport</h2>
                  <p className="text-slate-700 text-lg leading-relaxed max-w-md mx-auto font-medium">
                    Transformer operative kartleggingsdata til et profesjonelt beslutningsunderlag for toppledelse og styret.
                  </p>
                </div>
                <Button size="lg" onClick={handleGenerate} disabled={isGenerating} className="px-16 py-8 text-xl font-black uppercase tracking-widest bg-indigo-600 hover:bg-indigo-500 border-none shadow-2xl shadow-indigo-500/30">
                  {isGenerating ? "Aggregerer Strategisk Innsikt..." : "Generer Styrerapport"}
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        ) : (
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} className="grid grid-cols-1 lg:grid-cols-4 gap-10">
            {/* Slide Navigator Sidebar */}
            <div className="lg:col-span-1 space-y-6">
              <Card className="border border-slate-200 shadow-2xl bg-white sticky top-24">
                <CardHeader title="Presentasjonsstruktur" subtitle="4 Strategiske Seksjoner" />
                <div className="p-3 space-y-1">
                  {presentation.slides.map((slide, idx) => (
                    <button
                      key={slide.id}
                      onClick={() => setActiveSlide(idx)}
                      className={`w-full flex items-center gap-4 px-5 py-4 rounded-[20px] text-[11px] font-black uppercase tracking-widest transition-all ${activeSlide === idx ? 'bg-slate-900 text-white shadow-xl scale-[1.02]' : 'text-slate-400 hover:bg-slate-50'}`}
                    >
                      <span className="w-6 opacity-30">0{slide.id}</span>
                      <span className="truncate">{slide.title}</span>
                    </button>
                  ))}
                </div>
                <div className="p-6 border-t border-slate-100">
                   <p className="text-[10px] font-black text-slate-600 uppercase tracking-widest mb-4">Rapport-Narrativ</p>
                   <p className="text-xs text-slate-700 leading-relaxed italic line-clamp-4">
                     "{presentation.narrative}"
                   </p>
                </div>
              </Card>
            </div>

            {/* Main Slide Content */}
            <div className="lg:col-span-3 space-y-8">
              <motion.div
                key={activeSlide}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="bg-white shadow-[0_30px_60px_-15px_rgba(0,0,0,0.1)] rounded-sm min-h-[700px] border border-slate-200 flex flex-col relative overflow-hidden"
              >
                {/* Slide Header */}
                <div className="h-24 bg-slate-900 text-white px-12 flex justify-between items-center">
                  <div className="flex items-center gap-4">
                    <SafeIcon name="Hexagon" className="text-indigo-500 text-3xl" />
                    <span className="font-black uppercase tracking-tighter text-xl">Board Presentation</span>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] text-indigo-400 font-black uppercase tracking-widest">{presentation.meta.period}</p>
                    <p className="text-xs font-bold text-slate-400">{presentation.meta.company}</p>
                  </div>
                </div>

                {/* Slide Body */}
                <div className="flex-1 p-16 space-y-10">
                  <div className="space-y-2">
                    <span className="text-indigo-600 font-black text-[10px] uppercase tracking-[0.4em]">Seksjon 0{presentation.slides[activeSlide].id}</span>
                    <h2 className="text-5xl font-black text-slate-900 tracking-tighter uppercase leading-none">
                      {presentation.slides[activeSlide].title}
                    </h2>
                    <div className="w-20 h-2 bg-indigo-600 rounded-full mt-6" />
                  </div>

                  {presentation.slides[activeSlide].content && (
                    <p className="text-2xl text-slate-800 leading-relaxed font-serif italic font-light">
                      "{presentation.slides[activeSlide].content}"
                    </p>
                  )}

                  {presentation.slides[activeSlide].metrics && (
                    <div className="grid grid-cols-2 gap-6">
                      {presentation.slides[activeSlide].metrics.map((m, i) => (
                        <div key={i} className="p-8 bg-slate-50 rounded-[32px] border border-slate-200 flex justify-between items-center">
                          <div>
                            <p className="text-[10px] font-black text-slate-600 uppercase tracking-widest mb-1">{m.label}</p>
                            <h4 className="text-4xl font-black text-slate-900">{m.val}%</h4>
                          </div>
                          <span className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest ${m.status === 'Critical' || m.status === 'High' ? 'bg-rose-100 text-rose-600' : 'bg-emerald-100 text-emerald-600'}`}>
                            {m.status}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  {presentation.slides[activeSlide].risks && (
                    <div className="space-y-4">
                      {presentation.slides[activeSlide].risks.map((r, i) => (
                        <div key={i} className="p-6 bg-rose-50 rounded-[24px] border border-rose-100 flex gap-6 items-center">
                           <div className="w-10 h-10 rounded-full bg-rose-600 text-white flex items-center justify-center shrink-0">
                             <SafeIcon name="AlertTriangle" />
                           </div>
                           <div>
                             <h4 className="font-black text-rose-900 uppercase tracking-tight">{r.title}</h4>
                             <p className="text-sm text-rose-800/60 font-medium">{r.impact}</p>
                           </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {presentation.slides[activeSlide].governance && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                       <div className="space-y-4">
                         <h4 className="text-xs font-black text-slate-600 uppercase tracking-widest">Anbefalte Tiltak</h4>
                         <div className="space-y-2">
                           {presentation.slides[activeSlide].actions.map((a, i) => (
                             <div key={i} className="flex gap-3 text-sm font-bold text-slate-700 items-start">
                               <SafeIcon name="Check" className="text-indigo-600 shrink-0 mt-1" />
                               <span>{a}</span>
                             </div>
                           ))}
                         </div>
                       </div>
                       <div className="bg-slate-900 p-8 rounded-[40px] text-white">
                         <h4 className="text-xs font-black text-indigo-400 uppercase tracking-widest mb-6">Styrevedtak kreves</h4>
                         <div className="space-y-4">
                           {presentation.slides[activeSlide].governance.map((g, i) => (
                             <div key={i} className="flex gap-4 items-start">
                                <div className="w-5 h-5 rounded border border-white/20 flex items-center justify-center shrink-0 mt-0.5">
                                  <div className="w-2 h-2 bg-indigo-500 rounded-full opacity-0" />
                                </div>
                                <span className="text-xs text-slate-200 leading-relaxed font-medium">{g}</span>
                             </div>
                           ))}
                         </div>
                       </div>
                    </div>
                  )}
                </div>

                {/* Slide Footer */}
                <div className="h-16 border-t border-slate-100 px-12 flex justify-between items-center text-[10px] font-black text-slate-600 uppercase tracking-widest">
                  <span>© 2024 PsychoWork Strategic Analysis</span>
                  <span>Side {activeSlide + 1} av {presentation.slides.length}</span>
                </div>
              </motion.div>

              {/* Talking Points Section */}
              <Card className="bg-indigo-600 border-none text-white overflow-hidden relative shadow-2xl">
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16" />
                <CardHeader 
                  title="Strategiske Talepunkter" 
                  subtitle="Manuskript for presentatør" 
                  icon={<SafeIcon name="MessageSquare" className="text-indigo-200" />} 
                />
                <CardContent className="space-y-4 relative z-10">
                  {presentation.slides[activeSlide].talkingPoints.map((point, i) => (
                    <div key={i} className="flex gap-4 p-4 bg-white/10 rounded-2xl border border-white/10 items-start group hover:bg-white/20 transition-all">
                      <div className="w-6 h-6 rounded-full bg-white text-indigo-600 flex items-center justify-center text-[10px] font-black shrink-0 mt-0.5">
                        {i + 1}
                      </div>
                      <p className="text-sm font-medium text-white leading-relaxed">"{point}"</p>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};